import { useState } from "react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { toast } from "@/components/ui/sonner";
import { isValidEmail, isValidPersonName, normalizeEmail } from "@/lib/validation";

export function MarketingContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business: "Restaurant",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!isValidPersonName(form.name)) {
      nextErrors.name = "Enter your name (letters only).";
    }
    if (!isValidEmail(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!String(form.message || "").trim() || String(form.message).trim().length < 10) {
      nextErrors.message = "Please write a short message (at least 10 characters).";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      toast.error(Object.values(nextErrors)[0]);
      return;
    }
    setErrors({});
    setSent(true);
    toast.success(`Thanks — email ${normalizeEmail(form.email)} noted. Write to hello@buildhof.com and we’ll reply within one business day.`);
  };

  return (
    <MarketingLayout activePath="/contact">
      <section className="contact-hero" data-testid="public-contact-content">
        <div className="wrap">
          <span className="eyebrow">Contact</span>
          <h1 className="headline" data-testid="public-contact-title">
            Questions before you start? <em>We&apos;re a small team — ask us directly.</em>
          </h1>
          <p className="sub" data-testid="public-contact-body">We usually reply within one business day.</p>
        </div>
      </section>

      <section className="contact-section" data-testid="contact-section">
        <div className="wrap">
          <div className="contact-grid">
            <div className="contact-info-card">
              <h3>Get in touch</h3>
              <div className="info-row">
                <span className="ic">✉</span>
                <p><strong>Email</strong>hello@buildhof.com</p>
              </div>
              <div className="info-row">
                <span className="ic">📍</span>
                <p><strong>Based in</strong>Munich, Germany — serving businesses across Europe</p>
              </div>
              <div className="info-row">
                <span className="ic">🏢</span>
                <p><strong>Part of</strong>Build Up Rise Up UG (haftungsbeschränkt)</p>
              </div>
              <div className="info-row">
                <span className="ic">💬</span>
                <p><strong>Already a customer?</strong>Priority support customers get live chat and a named contact from their dashboard.</p>
              </div>
            </div>

            <form className="contact-form" onSubmit={submit} data-testid="contact-form" noValidate>
              <div className="form-two">
                <div className="form-row">
                  <label htmlFor="contact-name">Your name</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    placeholder="Jane Müller"
                    value={form.name}
                    maxLength={80}
                    onChange={(event) => setField("name", event.target.value)}
                    aria-invalid={Boolean(errors.name)}
                  />
                  {errors.name ? <p className="form-error" data-testid="contact-name-error">{errors.name}</p> : null}
                </div>
                <div className="form-row">
                  <label htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    inputMode="email"
                    placeholder="jane@example.com"
                    value={form.email}
                    maxLength={254}
                    onChange={(event) => setField("email", event.target.value)}
                    aria-invalid={Boolean(errors.email)}
                  />
                  {errors.email ? <p className="form-error" data-testid="contact-email-error">{errors.email}</p> : null}
                </div>
              </div>
              <div className="form-row">
                <label htmlFor="contact-business">Business type</label>
                <select
                  id="contact-business"
                  data-testid="contact-business-select"
                  value={form.business}
                  onChange={(event) => setField("business", event.target.value)}
                >
                  <option>Restaurant</option>
                  <option>Bakery</option>
                  <option>Florist</option>
                  <option>Café or other local shop</option>
                  <option>Not sure yet</option>
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-textarea"
                  placeholder="Tell us a bit about your business and what you're looking for."
                  value={form.message}
                  maxLength={2000}
                  onChange={(event) => setField("message", event.target.value)}
                  aria-invalid={Boolean(errors.message)}
                />
                {errors.message ? <p className="form-error" data-testid="contact-message-error">{errors.message}</p> : null}
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center" }}
                data-testid="contact-submit-button"
              >
                {sent ? "Message noted" : "Send message"}
              </button>
              {sent ? (
                <p style={{ marginTop: 12, fontSize: 13.5, color: "var(--text-secondary)" }} data-testid="contact-sent-note">
                  Email delivery isn&apos;t connected yet — please write to{" "}
                  <a href="mailto:hello@buildhof.com" style={{ color: "var(--terracotta)", fontWeight: 600 }}>hello@buildhof.com</a>.
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
